import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrashIcon, PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Tooltip } from 'react-tooltip';
import Cookies from 'js-cookie';

const BudgetTracker = () => {
    const [marriageBudget, setMarriageBudget] = useState(0);
    const [isBudgetSet, setIsBudgetSet] = useState(false);
    const [budgetId, setBudgetId] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [newExpenseName, setNewExpenseName] = useState('');
    const [newExpenseAmount, setNewExpenseAmount] = useState(0);
    const [editingExpense, setEditingExpense] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const fetchBudget = async () => {
        try {
            const user = JSON.parse(Cookies.get("user"));
            const userId = user._id;

            const response = await axios.get(`${process.env.REACT_APP_ADDRESS}/budgets/${userId}`, {
                headers: { Authorization: `Bearer ${Cookies.get('token')}` },
            });

            if (response.data.length > 0) {
                const budget = response.data[0];
                setMarriageBudget(budget.totalAmount);
                setExpenses(budget.expenses);
                setBudgetId(budget._id);
                setIsBudgetSet(true);
            }
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch budget');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBudget();
    }, []);

    const handleMarriageBudgetSubmit = async () => {
        if (marriageBudget > 0) {
            try {
                const user = JSON.parse(Cookies.get("user"));
                const userId = user._id;

                if (budgetId) {
                    await axios.put(`${process.env.REACT_APP_ADDRESS}/budgets/${budgetId}`, {
                        totalAmount: marriageBudget
                    }, {
                        headers: { Authorization: `Bearer ${Cookies.get('token')}` }
                    });
                } else {
                    const response = await axios.post(`${process.env.REACT_APP_ADDRESS}/budgets/create`, {
                        userId,
                        budgetName: 'Marriage Budget',
                        totalAmount: marriageBudget
                    }, {
                        headers: { Authorization: `Bearer ${Cookies.get('token')}` }
                    });
                    setBudgetId(response.data._id);
                }
                setIsBudgetSet(true);
                fetchBudget();
            } catch (err) {
                console.error('Failed to save budget:', err.response ? err.response.data : err.message);
                setError('Failed to save budget');
            }
        } else {
            setError('Please enter a valid budget amount.');
        }
    };

    const addNewExpense = async () => {
        if (newExpenseName && newExpenseAmount > 0) {
            try {
                const response = await axios.post(`${process.env.REACT_APP_ADDRESS}/budgets/${budgetId}/expenses`, {
                    expenseName: newExpenseName,
                    amount: newExpenseAmount
                }, {
                    headers: { Authorization: `Bearer ${Cookies.get('token')}` }
                });
                setExpenses([...expenses, {
                    id: response.data.expenses[response.data.expenses.length - 1]._id,
                    expenseName: newExpenseName,
                    amount: newExpenseAmount
                }]);
                setNewExpenseName('');
                setNewExpenseAmount(0);
            } catch (err) {
                setError('Failed to add expense');
            } finally {
                fetchBudget();
            }
        } else {
            setError('Please enter a valid expense name and amount.');
        }
    };

    const startEditing = (expense) => {
        setEditingExpense({ ...expense });
    };

    const saveEditedExpense = async () => {
        if (editingExpense) {
            try {
                await axios.put(`${process.env.REACT_APP_ADDRESS}/budgets/${budgetId}/expenses/${editingExpense._id}`, {
                    expenseName: editingExpense.expenseName,
                    amount: editingExpense.amount
                }, {
                    headers: { Authorization: `Bearer ${Cookies.get('token')}` }
                });
                setExpenses(expenses.map(e =>
                    e.id === editingExpense._id ? editingExpense : e
                ));
                setEditingExpense(null);
            } catch (err) {
                setError('Failed to save expense');
            } finally {
                fetchBudget();
            }
        }
    };

    const deleteExpense = async (id) => {
        try {
            await axios.delete(`${process.env.REACT_APP_ADDRESS}/budgets/${budgetId}/expenses/${id}`, {
                headers: { Authorization: `Bearer ${Cookies.get('token')}` }
            });
            setExpenses(expenses.filter(e => e.id !== id));
        } catch (err) {
            setError('Failed to delete expense');
        } finally {
            fetchBudget();
        }
    };

    const resetBudget = () => {
        setIsBudgetSet(false);
    };

    const totalExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    const remaining = marriageBudget - totalExpenses;

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!isBudgetSet) {
        return (
            <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-xl">
                <h2 className="text-2xl font-bold mb-6 text-center">Enter Your Marriage Budget</h2>
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="budget">
                        ESTIMATED COST
                    </label>
                    <div className="flex items-center border-b border-pink-500 py-2">
                        <span className="text-2xl mr-2">DT</span>
                        <input
                            className="appearance-none bg-transparent border-none w-full text-gray-700 mr-3 py-1 px-2 leading-tight focus:outline-none text-2xl"
                            type="number"
                            id="budget"
                            value={marriageBudget || ''}
                            onChange={(e) => setMarriageBudget(Number(e.target.value))}
                            placeholder="32,000"
                        />
                    </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">You can edit this at any time.</p>
                <button
                    className="w-full bg-pink-500 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                    onClick={handleMarriageBudgetSubmit}
                >
                    Save budget
                </button>
                {error && <p className="text-red-500 text-xs italic mt-2">{error}</p>}
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-4">
            <h2 className="text-3xl font-bold mb-6">Budget Tracker</h2>
            <div className="mb-4">
                <p className="text-xl font-bold">Total Budget: {marriageBudget} DT</p>
                <p className={`text-xl font-bold ${remaining < 0 ? 'text-red-500' : ''}`}>Remaining: {remaining} DT</p>
            </div>
            <div className="flex justify-end mb-4">
                <button
                    data-tooltip-id="updateBudget"
                    onClick={resetBudget}
                    className="bg-pink-500 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded flex items-center"
                    data-tooltip-content="Update Budget"
                    data-tooltip-float="true"
                >
                    <PlusIcon className="h-5 w-5" />
                </button>
                <Tooltip id="updateBudget" />
            </div>
            <table className="w-full mb-4">
                <thead>
                    <tr className="bg-gray-200">
                        <th className="p-2 text-left">EXPENSE</th>
                        <th className="p-2 text-right">ESTIMATED COST</th>
                        <th className="p-2 text-right">ACTIONS</th>
                    </tr>
                </thead>
                <tbody>
                    {expenses.map(expense => (
                        <tr key={expense._id} className="border-b">
                            <td className="p-2">
                                {editingExpense && editingExpense._id === expense._id ? (
                                    <input
                                        value={editingExpense.expenseName}
                                        onChange={(e) => setEditingExpense({ ...editingExpense, expenseName: e.target.value })}
                                        className="border p-1 w-full"
                                    />
                                ) : expense.expenseName}
                            </td>
                            <td className="p-2 text-right">
                                {editingExpense && editingExpense._id === expense._id ? (
                                    <input
                                        type="number"
                                        value={editingExpense.amount}
                                        onChange={(e) => setEditingExpense({ ...editingExpense, amount: Number(e.target.value) })}
                                        className="border p-1 w-full text-right"
                                    />
                                ) : `${expense.amount} DT`}
                            </td>
                            <td className="p-2 text-right">
                                {editingExpense && editingExpense._id === expense._id ? (
                                    <button onClick={saveEditedExpense} className="text-indigo-500 mr-2">Save</button>
                                ) : (
                                    <>
                                        <button onClick={() => startEditing(expense)} className="text-indigo-500 mr-2">
                                            <PencilIcon className="h-5 w-5 inline" />
                                        </button>
                                        <button onClick={() => deleteExpense(expense._id)} className="text-red-500">
                                            <TrashIcon className="h-5 w-5 inline" />
                                        </button>
                                    </>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className="font-bold">
                        <td className="p-2">Total:</td>
                        <td className="p-2 text-right">{totalExpenses} DT</td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
            <div className="mb-4">
                <input
                    type="text"
                    value={newExpenseName}
                    onChange={(e) => setNewExpenseName(e.target.value)}
                    placeholder="New Expense Name"
                    className="border p-2 mr-2"
                />
                <input
                    type="number"
                    value={newExpenseAmount}
                    onChange={(e) => setNewExpenseAmount(Number(e.target.value))}
                    placeholder="Amount"
                    className="border p-2 mr-2"
                />
                <button onClick={addNewExpense} className="bg-pink-500 text-white p-2 rounded">
                    Add New Expense
                </button>
            </div>
            {error && <p className="text-red-500">{error}</p>}
        </div>
    );
};

export default BudgetTracker;
